/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/itsm.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Which service-management system. Closed, and a member is added with a connector rather than by
 * configuration — a provider name the deployment cannot reach is a connection that fails at first
 * ticket with an error naming the wrong layer.
 */
export type ItsmProvider = 'servicenow' | 'jira';

export const ITSM_PROVIDERS: readonly ItsmProvider[] = ['servicenow', 'jira'];

export function isItsmProvider(value: unknown): value is ItsmProvider {
  return (['servicenow', 'jira'] as readonly unknown[]).includes(value);
}

/**
 * How Lumiq authenticates to the provider. `basic` is a username and password — ServiceNow's
 * integration user. `api_token` is an account identifier and a token — Jira Cloud's email plus API
 * token, sent as Basic auth on the wire but a different credential with a different lifecycle,
 * which is why it is a different member rather than the same one. OAuth is deliberately absent in
 * V1 rather than present and unimplemented. See the document description.
 */
export type AuthType = 'basic' | 'api_token';

export const AUTH_TYPES: readonly AuthType[] = ['basic', 'api_token'];

export function isAuthType(value: unknown): value is AuthType {
  return (['basic', 'api_token'] as readonly unknown[]).includes(value);
}

/**
 * `registered` means never contacted — registration does not call the provider. `unreachable` is
 * the result of a *failed* verification and is distinct from `disabled`, which is somebody's
 * decision. A screen that showed one for the other would report a maintenance window as an outage.
 */
export type ConnectionState = 'registered' | 'verified' | 'unreachable' | 'disabled';

export const CONNECTION_STATES: readonly ConnectionState[] = ['registered', 'verified', 'unreachable', 'disabled'];

export function isConnectionState(value: unknown): value is ConnectionState {
  return (['registered', 'verified', 'unreachable', 'disabled'] as readonly unknown[]).includes(value);
}

export interface ProviderProfile {
  readonly provider: ItsmProvider;
  readonly display_name: string;
  /** Whether this build has a connector wired for it. */
  readonly available: boolean;
  readonly auth_types: readonly AuthType[];
  /**
   * Why it is unavailable, when it is. Null when available — an honest absence rather than an
   * empty sentence.
   */
  readonly detail: string | null;
}

export function isProviderProfile(value: unknown): value is ProviderProfile {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['servicenow', 'jira'] as readonly unknown[]).includes(candidate['provider']) &&
    typeof candidate['display_name'] === 'string' &&
    typeof candidate['available'] === 'boolean' &&
    Array.isArray(candidate['auth_types']) &&
    (candidate['auth_types'] as readonly unknown[]).every((item) => isAuthType(item)) &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null)
  );
}

/**
 * Every provider this build knows, available or not. A named envelope rather than an anonymous
 * one, and the reason is the generated client: only a `components/schemas` entry is emitted, so an
 * inline object leaves a screen hand-writing the one guard that decides whether a response is
 * readable. That is the drift the generator exists to prevent, and section 9 records it as the gap
 * no sweep over the schema graph can see — a response type a screen wants and nobody named.
 */
export interface ProviderList {
  readonly providers: readonly ProviderProfile[];
}

export function isProviderList(value: unknown): value is ProviderList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['providers']) &&
    (candidate['providers'] as readonly unknown[]).every((item) => isProviderProfile(item))
  );
}

export interface ItsmConnection {
  readonly id: string;
  readonly provider: ItsmProvider;
  /** What an operator calls it — "ServiceNow — Production". */
  readonly name: string;
  /**
   * The provider's base URL. **Not secret**, and stored rather than inferred: it is half of the
   * ticket's deep link and it is what the deployment's allow-list is checked against.
   */
  readonly instance_url: string;
  readonly auth_type: AuthType;
  readonly state: ConnectionState;
  readonly created_by: string;
  readonly created_at: string;
  /**
   * When a verification was last *attempted*, successful or not. Null means never — which is a
   * different fact from "verified long ago" and the screen says so.
   */
  readonly last_checked_at: string | null;
  /**
   * A classification of the last failure — `unauthorized`, `forbidden`, `unreachable`,
   * `rate_limited`. **Never the provider's own message**, which carries the instance URL and can
   * carry a token on a redirect.
   */
  readonly last_check_detail: string | null;
  /** Jira only. The project new issues are created in. */
  readonly project_key: string | null;
  /** Jira only. The issue type new issues are created as. */
  readonly issue_type: string | null;
}

export function isItsmConnection(value: unknown): value is ItsmConnection {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['servicenow', 'jira'] as readonly unknown[]).includes(candidate['provider']) &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['instance_url'] === 'string' &&
    (['basic', 'api_token'] as readonly unknown[]).includes(candidate['auth_type']) &&
    (['registered', 'verified', 'unreachable', 'disabled'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['created_at'] === 'string' &&
    (typeof candidate['last_checked_at'] === 'string' || candidate['last_checked_at'] === null) &&
    (typeof candidate['last_check_detail'] === 'string' || candidate['last_check_detail'] === null) &&
    (typeof candidate['project_key'] === 'string' || candidate['project_key'] === null) &&
    (typeof candidate['issue_type'] === 'string' || candidate['issue_type'] === null)
  );
}

/**
 * Every ITSM connection this tenant has registered. Not paged, and that is a bound rather than an
 * omission: `MAX_RULES` is 10 and a tenant with more service desks than that is expressing
 * something the ticket-rule vocabulary cannot say. A cursor here would be machinery for a list
 * that fits on one screen by construction.
 */
export interface ItsmConnectionList {
  readonly connections: readonly ItsmConnection[];
}

export function isItsmConnectionList(value: unknown): value is ItsmConnectionList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['connections']) &&
    (candidate['connections'] as readonly unknown[]).every((item) => isItsmConnection(item))
  );
}

export interface ItsmCredentials {
  readonly auth_type: AuthType;
  /**
   * The ServiceNow integration user, or the Jira account email. Half of the credential and useless
   * without the other half, which is why it travels with it rather than being stored as metadata.
   */
  readonly username?: string;
  /** ServiceNow. Sealed into Vault; appears in no response. */
  readonly password?: string;
  /**
   * The opaque bearer half of an `api_token` credential - a Jira Cloud API token. Sealed into
   * Vault; appears in no response. **Named `token`, not `api_token`**, because the field is the
   * slot it occupies in the KV document rather than a claim about what the string means, and
   * `EnvironmentSecretResolver` already carries that slot. `AuthType` has already said which kind
   * of credential this is. The spec declared `api_token` while `REQUIRED_FIELDS` asked for
   * `token`, so every Jira registration was refused for a field the client had sent under the
   * documented name.
   */
  readonly token?: string;
}

export function isItsmCredentials(value: unknown): value is ItsmCredentials {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['basic', 'api_token'] as readonly unknown[]).includes(candidate['auth_type'])
  );
}

export interface ItsmConnectionCreate {
  readonly provider: ItsmProvider;
  readonly name: string;
  readonly instance_url: string;
  readonly project_key?: string | null;
  readonly issue_type?: string | null;
  readonly credentials: ItsmCredentials;
}

export function isItsmConnectionCreate(value: unknown): value is ItsmConnectionCreate {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['servicenow', 'jira'] as readonly unknown[]).includes(candidate['provider']) &&
    typeof candidate['name'] === 'string' &&
    typeof candidate['instance_url'] === 'string' &&
    candidate['credentials'] !== undefined
  );
}

export interface VerificationResult {
  readonly reachable: boolean;
  readonly state: ConnectionState;
  /** A classification when unreachable, null when fine. */
  readonly detail: string | null;
  readonly checked_at: string;
}

export function isVerificationResult(value: unknown): value is VerificationResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['reachable'] === 'boolean' &&
    (['registered', 'verified', 'unreachable', 'disabled'] as readonly unknown[]).includes(candidate['state']) &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    typeof candidate['checked_at'] === 'string'
  );
}

/**
 * The same vocabulary `observability.yaml` publishes, because a rule that filtered on a different
 * set would be a second answer to "what kinds of incident exist".
 */
export type IncidentKind = 'schema_drift' | 'freshness_breach' | 'volume_anomaly' | 'assessment_overdue' | 'contract_breach' | 'reconciliation_break' | 'rule_persistent_failure' | 'harvest_schedule_failing' | 'execution_schedule_failing' | 'reconciliation_schedule_failing';

export const INCIDENT_KINDS: readonly IncidentKind[] = ['schema_drift', 'freshness_breach', 'volume_anomaly', 'assessment_overdue', 'contract_breach', 'reconciliation_break', 'rule_persistent_failure', 'harvest_schedule_failing', 'execution_schedule_failing', 'reconciliation_schedule_failing'];

export function isIncidentKind(value: unknown): value is IncidentKind {
  return (['schema_drift', 'freshness_breach', 'volume_anomaly', 'assessment_overdue', 'contract_breach', 'reconciliation_break', 'rule_persistent_failure', 'harvest_schedule_failing', 'execution_schedule_failing', 'reconciliation_schedule_failing'] as readonly unknown[]).includes(value);
}

export type Severity = 'info' | 'warning' | 'critical';

export const SEVERITYS: readonly Severity[] = ['info', 'warning', 'critical'];

export function isSeverity(value: unknown): value is Severity {
  return (['info', 'warning', 'critical'] as readonly unknown[]).includes(value);
}

export interface TicketRule {
  readonly id: string;
  readonly connection_id: string;
  /**
   * Resolved here rather than left to the client. A rule screen that rendered a bare UUID for the
   * destination is the defect this codebase has shipped four times.
   */
  readonly connection_name: string;
  readonly provider: ItsmProvider;
  /**
   * Null means every kind. Nullable rather than an `all` member, for the reason `NotificationRule`
   * gives: an `all` member would sort into the constraint's list and could be stored as an
   * incident's own kind.
   */
  readonly incident_kind: IncidentKind | null;
  /**
   * A floor — this severity and anything worse. There is deliberately no ceiling, for
   * `NotificationRule`'s reason: every reading of "warnings but not criticals" is somebody about
   * to miss an outage.
   */
  readonly min_severity: Severity;
  readonly enabled: boolean;
  readonly created_by: string;
  readonly created_at: string;
}

export function isTicketRule(value: unknown): value is TicketRule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['connection_id'] === 'string' &&
    typeof candidate['connection_name'] === 'string' &&
    (['servicenow', 'jira'] as readonly unknown[]).includes(candidate['provider']) &&
    (candidate['incident_kind'] === null || isIncidentKind(candidate['incident_kind'])) &&
    (['info', 'warning', 'critical'] as readonly unknown[]).includes(candidate['min_severity']) &&
    typeof candidate['enabled'] === 'boolean' &&
    typeof candidate['created_by'] === 'string' &&
    typeof candidate['created_at'] === 'string'
  );
}

export interface TicketRuleCreate {
  readonly connection_id: string;
  readonly incident_kind?: IncidentKind | null;
  readonly min_severity: Severity;
  readonly enabled?: boolean;
}

export function isTicketRuleCreate(value: unknown): value is TicketRuleCreate {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['connection_id'] === 'string' &&
    (['info', 'warning', 'critical'] as readonly unknown[]).includes(candidate['min_severity'])
  );
}

export interface TicketPolicy {
  /**
   * Whether this tenant has declared any rule. False with an empty list means nobody has chosen;
   * **the default is no tickets**, so an undeclared tenant's behaviour is unchanged from before
   * this module existed.
   */
  readonly declared: boolean;
  readonly rules: readonly TicketRule[];
}

export function isTicketPolicy(value: unknown): value is TicketPolicy {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['declared'] === 'boolean' &&
    Array.isArray(candidate['rules']) &&
    (candidate['rules'] as readonly unknown[]).every((item) => isTicketRule(item))
  );
}

/**
 * `retired` is not a failure. It is what a job becomes when its connection was deleted or disabled
 * while it waited — nobody did anything wrong, and recording it as `failed` would send somebody
 * looking for a provider outage that never happened.
 */
export type TicketJobState = 'pending' | 'running' | 'succeeded' | 'failed' | 'retired';

export const TICKET_JOB_STATES: readonly TicketJobState[] = ['pending', 'running', 'succeeded', 'failed', 'retired'];

export function isTicketJobState(value: unknown): value is TicketJobState {
  return (['pending', 'running', 'succeeded', 'failed', 'retired'] as readonly unknown[]).includes(value);
}

export interface TicketReference {
  readonly provider: ItsmProvider;
  /**
   * What a human calls the ticket — ServiceNow's `INC0012345`, Jira's `LUMIQ-142`. The opaque
   * internal id is deliberately not published: it identifies nothing a reader can use, and the key
   * plus the URL is what somebody needs to open it.
   */
  readonly external_key: string;
  /** The deep link. Built from the connection's instance URL and the key. */
  readonly external_url: string;
  readonly created_at: string;
  readonly connection_name: string;
}

export function isTicketReference(value: unknown): value is TicketReference {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['servicenow', 'jira'] as readonly unknown[]).includes(candidate['provider']) &&
    typeof candidate['external_key'] === 'string' &&
    typeof candidate['external_url'] === 'string' &&
    typeof candidate['created_at'] === 'string' &&
    typeof candidate['connection_name'] === 'string'
  );
}

export interface IncidentTicketStatus {
  /**
   * False when this tenant's policy files no ticket for this incident. **A decision, not a
   * failure**, and the field exists so a screen can say which — the same distinction
   * `NotificationOutcome.attempted` draws for an alert that was deliberately not sent.
   */
  readonly attempted: boolean;
  /** Null when nothing was attempted. */
  readonly state: TicketJobState | null;
  /** Present only once a ticket exists. */
  readonly ticket: TicketReference | null;
  /** A failure classification, never the provider's message. */
  readonly detail: string | null;
  readonly attempts: number;
  /**
   * When the queue will try again. Null for a terminal job — including a succeeded one, where a
   * date would read as a pending retry.
   */
  readonly next_attempt_at: string | null;
}

export function isIncidentTicketStatus(value: unknown): value is IncidentTicketStatus {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['attempted'] === 'boolean' &&
    (candidate['state'] === null || isTicketJobState(candidate['state'])) &&
    (candidate['ticket'] === null || isTicketReference(candidate['ticket'])) &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    typeof candidate['attempts'] === 'number' &&
    (typeof candidate['next_attempt_at'] === 'string' || candidate['next_attempt_at'] === null)
  );
}

/**
 * One incident's ticket status, carrying the incident it belongs to so a list can be joined
 * without relying on its order.
 */
export interface IncidentTicketSummary {
  readonly incident_id: string;
  /**
   * Always true here. An incident with nothing attempted is absent from the list rather than
   * present with `false` — the caller knows which ids it asked for, so sending a row per absence
   * would be sending the question back with the answer. The field is kept so this schema and
   * `IncidentTicketStatus` describe the same shape.
   */
  readonly attempted: boolean;
  readonly state: TicketJobState;
  readonly ticket: TicketReference | null;
  /** A failure classification, never the provider's message. */
  readonly detail: string | null;
  readonly attempts: number;
  readonly next_attempt_at: string | null;
}

export function isIncidentTicketSummary(value: unknown): value is IncidentTicketSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['incident_id'] === 'string' &&
    typeof candidate['attempted'] === 'boolean' &&
    (['pending', 'running', 'succeeded', 'failed', 'retired'] as readonly unknown[]).includes(candidate['state']) &&
    (candidate['ticket'] === null || isTicketReference(candidate['ticket'])) &&
    (typeof candidate['detail'] === 'string' || candidate['detail'] === null) &&
    typeof candidate['attempts'] === 'number' &&
    (typeof candidate['next_attempt_at'] === 'string' || candidate['next_attempt_at'] === null)
  );
}

export interface IncidentTicketList {
  readonly items: readonly IncidentTicketSummary[];
}

export function isIncidentTicketList(value: unknown): value is IncidentTicketList {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isIncidentTicketSummary(item))
  );
}
